import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  discardShowcaseItem,
  getFeedShowcase,
  refreshFeedShowcase,
} from "../../utils/showcaseClientCache";
import { markImageFailed } from "../../utils/imageProbe";
import "./ShoppingNow.scss";

const PAGE_SIZE = 12;

const ProductThumb = ({ item, onDead }) => {
  const images = item?.images || [];
  const [index, setIndex] = useState(0);
  const src = images[index];
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => {
        markImageFailed(src);
        if (index + 1 < images.length) {
          setIndex(index + 1);
          return;
        }
        if (onDead) onDead(item);
      }}
    />
  );
};

const ShoppingNow = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.08 });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasLoaded, setHasLoaded] = useState(false);

  const applyItems = (unique) => {
    setItems(unique);
    if (!unique.length) {
      setError("No shopping finds to show right now. Try again in a moment.");
    } else {
      setError("");
    }
  };

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError("");
    try {
      const unique = refresh
        ? await refreshFeedShowcase(PAGE_SIZE)
        : await getFeedShowcase(PAGE_SIZE);
      applyItems(unique);
      setHasLoaded(true);
    } catch (_) {
      setError("Could not load what people are shopping for.");
      setItems([]);
      setHasLoaded(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (inView && !hasLoaded) load(false);
  }, [inView, hasLoaded, load]);

  const dropItem = (item) => {
    if (!item?.id) return;
    discardShowcaseItem(item);
    setItems((prev) => prev.filter((p) => p.id !== item.id));
  };

  return (
    <motion.section
      id="shopping-now"
      className="shopping-now"
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: inView ? 1 : 0, y: inView ? 0 : 24 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <div className="shopping-now__inner">
        <div className="shopping-now__header">
          <div>
            <p className="shopping-now__eyebrow">Live carts</p>
            <h2 className="shopping-now__title">
              See what people are shopping for
            </h2>
            <p className="shopping-now__lead">
              A rotating look at real finds saved in OllaCart. Refresh anytime
              for a new set.
            </p>
          </div>
          <button
            type="button"
            className="shopping-now__refresh"
            onClick={() => load(true)}
            disabled={loading}
          >
            {loading ? "Loading…" : "Show me more"}
          </button>
        </div>

        {error ? <p className="shopping-now__error">{error}</p> : null}

        <ul className="shopping-now__grid">
          {items.map((item) => {
            const card = (
              <>
                <div className="shopping-now__media">
                  <ProductThumb item={item} onDead={dropItem} />
                </div>
                <span className="shopping-now__name">{item.name}</span>
              </>
            );
            return (
              <li key={item.id}>
                {item.url ? (
                  <a
                    className="shopping-now__card"
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {card}
                  </a>
                ) : (
                  <div className="shopping-now__card">{card}</div>
                )}
              </li>
            );
          })}
        </ul>

        {!loading && items.length > 0 ? (
          <div className="shopping-now__footer">
            <button
              type="button"
              className="shopping-now__refresh shopping-now__refresh--ghost"
              onClick={() => load(true)}
            >
              Refresh for more finds
            </button>
          </div>
        ) : null}
      </div>
    </motion.section>
  );
};

export default ShoppingNow;
